// src/components/SkuPicker.jsx
import React, { useEffect, useState } from 'react';

const API = import.meta.env.VITE_API_URL;

export default function SkuPicker({
  isOpen,
  clientId,
  initialQuery = '',
  onClose,
  onSelect
}) {
  const [q, setQ] = useState(initialQuery);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoverIndex, setHoverIndex] = useState(null); // <— new for hover effect

  const fetchJSON = async (url) => {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return res.json();
  };

  useEffect(() => {
    if (!isOpen) return;
    setQ(initialQuery || '');
    if (!clientId) { setRows([]); return; }

    const run = async () => {
      setLoading(true);
      try {
        const url = q && q.trim().length >= 2
          ? `${API}/api/items/lookupbysku?sku=${encodeURIComponent(q.trim())}&clientid=${clientId}`
          : `${API}/api/items?clientid=${clientId}`;
        const data = await fetchJSON(url);
        setRows(Array.isArray(data) ? data : (data.data || []));
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [isOpen, clientId, initialQuery]);

  const doSearch = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const url = q && q.trim().length >= 2
        ? `${API}/api/items/lookupbysku?sku=${encodeURIComponent(q.trim())}&clientid=${clientId}`
        : `${API}/api/items?clientid=${clientId}`;
      const data = await fetchJSON(url);
      setRows(Array.isArray(data) ? data : (data.data || []));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={backdropStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={hdrStyle}>
          <h3 style={{ margin: 0 }}>Select SKU</h3>
          <button style={closeBtn} onClick={onClose} aria-label="Close picker">✕</button>
        </div>

        {!clientId && (
          <div style={{ padding: '8px 0', color: 'var(--danger-color, #b00)' }}>
            Choose a client on the receipt first to load SKUs.
          </div>
        )}

        <div style={toolbarStyle}>
          <input
            style={inputStyle}
            placeholder="Search by SKU or description (min 2 chars for search)…"
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') doSearch(); }}
          />
          <button style={btnStyle} onClick={doSearch} disabled={!clientId || loading}>
            {loading ? 'Searching…' : 'Search / List'}
          </button>
        </div>

        <div style={tableWrap}>
          <table style={tableStyle}>
            <thead>
              <tr style={theadRowStyle}>
                <th style={{ width: 100, textAlign: 'left' }}>Action</th>
                <th style={{ width: '18%' }}>SKU</th>
                <th>Description</th>
                <th style={{ width: '16%' }}>Part #</th>
                <th style={{ width: '16%' }}>UPC</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5}>Loading…</td></tr>
              ) : rows.length ? rows.map((item, idx) => (
                <tr
                  key={item.itemid}
                  style={{
                    ...(idx % 2 === 0 ? rowStyle : altRowStyle),
                    ...(hoverIndex === idx ? hoverRowStyle : {})
                  }}
                  onMouseEnter={() => setHoverIndex(idx)}
                  onMouseLeave={() => setHoverIndex(null)}
                >
                  <td>
                    <button
                      style={miniBtn}
                      onClick={() => onSelect?.(item)}
                    >
                      Select
                    </button>
                  </td>
                  <td className="nowrap">{item.sku}</td>
                  <td>{item.description}</td>
                  <td className="nowrap">{item.partnumber}</td>
                  <td className="nowrap">{item.upc}</td>
                </tr>
              )) : (
                <tr><td colSpan={5} style={{ color: 'var(--muted-color, #888)' }}>No results</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* Theme-aware inline styles with hover support */
const backdropStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
};
const modalStyle = {
  background: 'var(--panel-bg, #fff)', color: 'var(--text-color, #1f2937)',
  borderRadius: 10, width: 'min(1000px, 95vw)', maxHeight: '90vh',
  overflow: 'hidden', boxShadow: '0 12px 32px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column'
};
const hdrStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 16px', borderBottom: '1px solid var(--border-color, #e5e7eb)'
};
const closeBtn = { border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: 'inherit' };
const toolbarStyle = { display: 'flex', gap: 8, padding: 12, borderBottom: '1px solid var(--border-color, #e5e7eb)' };
const inputStyle = {
  flex: 1, padding: '8px 10px', border: '1px solid var(--border-color, #d1d5db)',
  borderRadius: 6, fontSize: 14, background: 'var(--input-bg, #fff)', color: 'inherit'
};
const btnStyle = {
  padding: '8px 12px', borderRadius: 6,
  border: '1px solid var(--primary-color, #0a6)',
  background: 'var(--primary-color, #0a6)', color: '#fff', cursor: 'pointer'
};
const tableWrap = { padding: 12, overflow: 'auto' };
const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const theadRowStyle = {
  background: 'var(--table-head-bg, #f3f4f6)',
  color: 'var(--table-head-fg, inherit)'
};
const rowStyle = {
  background: 'var(--table-row-bg, #fff)',
  borderBottom: '1px solid var(--border-color, #eef2f7)',
  transition: 'background 0.15s ease'
};
const altRowStyle = {
  background: 'var(--table-row-alt-bg, #f9fafb)',
  borderBottom: '1px solid var(--border-color, #eef2f7)',
  transition: 'background 0.15s ease'
};
const hoverRowStyle = {
  background: 'var(--table-row-hover-bg, rgba(0,0,0,0.08))'
};
const miniBtn = {
  padding: '6px 8px', borderRadius: 6,
  border: '1px solid var(--accent-color, #0891b2)',
  background: 'var(--accent-color, #0891b2)', color: '#fff', cursor: 'pointer'
};
